/**
 * Qué hacer al entrar a la pantalla que crea la cartera: esperar, navegar o postear.
 *
 * Vive afuera del componente y devuelve una decisión en vez de ejecutarla porque es exactamente
 * donde estaba el defecto: el guard de idempotencia era una condición suelta adentro de un
 * `useEffect` —imposible de probar sin montar la pantalla con su router y su contexto—, y así nadie
 * vio que **ningún camino devolvía el estado a `idle`**. Con el alta ya fallada, el efecto salía por
 * el guard, no posteaba y no navegaba: la pantalla se quedaba en «Preparando…» para siempre y el
 * reintento que promete el banner de error no existía.
 *
 * **Un POST sale sólo de un gesto del usuario**: el primer ingreso (`idle`) o un pedido explícito
 * (`requested`, que dejan «Crear…» y «Crear el resto»). Hasta el 24/09 la regla era «el mismo pedido
 * no se repite; uno distinto sí», decidida por la FIRMA de la cartera, y eso reposteaba sin que nadie
 * apretara nada: al fallar, `APPLY_FAILED` adopta como existentes los clientes que el alta alcanzó a
 * crear, la firma cambia, y el efecto —que corre antes de que termine la navegación— lo leía como
 * «otra cartera». Medido en producción: dos POST seguidos (140 ids de línea creados y borrados) y el
 * segundo borraba el error del primero, así que la pantalla volvía muda al paso 3. Cualquier cambio
 * de estado que toque las decisiones reabría la puerta; un gesto explícito la cierra por diseño.
 *
 * ⚠️ Una excepción, PREEXISTENTE y no cerrada acá: `apply` no se persiste (`wizardStorage` guarda sólo
 * las decisiones), así que un F5 en `/onboarding/creando` vuelve a `idle` y postea el borrador de
 * nuevo. Lo amortigua el backend: las cuentas ya cubiertas se saltean (no hay líneas duplicadas), los
 * nombres de clientes ya creados rebotan con `CLIENT_NAME_TAKEN` y el cupo se valida antes de
 * escribir. Cerrarla del todo pide persistir un marcador de «alta en vuelo» (review C-TUNELDEUDA-05).
 */

/** Qué cartera es ésta. Alcanza con el contenido: si cambió, es otro pedido. */
export function signatureOf(items) {
    return JSON.stringify(items || []);
}

export function applyPlanFor({status, rowErrors, hasItems, postInFlight = true}) {
    // `running` sin un POST vivo es un alta HUÉRFANA: el usuario apretó «Guardar y seguir después»
    // con el pedido en vuelo y la respuesta cayó sobre una pantalla que ya no estaba. Esperarla es
    // esperar para siempre —el caso que dejaba «Preparando…» colgado—, así que se sigue el progreso
    // desde el servidor, que sabe qué líneas se crearon aunque la pantalla haya perdido los ids.
    if (status === 'running' && !postInFlight) return {action: 'track'};
    // Pedido en vuelo o sincronizando: la pantalla tiene algo que mostrar y nada que decidir.
    if (status === 'running' || status === 'syncing') return {action: 'wait'};

    if (status === 'idle' || status === 'requested') {
        if (!hasItems) return {action: 'navigate', to: '/onboarding/clientes'};
        return {action: 'post'};
    }

    // Terminó (bien o con errores) y nadie pidió de nuevo. El usuario llegó a una pantalla de espera
    // que no tiene nada que esperar —volviendo con el botón del navegador, casi siempre—, así que se
    // lo lleva a donde el resultado significa algo. Aunque la cartera haya cambiado: sin gesto, no
    // hay POST.
    return {action: 'navigate',
            to: status === 'done' ? '/onboarding/listo' : destinationForErrors(rowErrors)};
}

/**
 * A qué paso volver según lo que falló.
 *
 * Un presupuesto o una conversión inválidos se arreglan en la pantalla de presupuestos —las dos cosas
 * se eligen ahí— y todo lo demás en la de clientes. Mandar siempre al principio obligaría a recorrer
 * de nuevo lo que ya estaba bien.
 */
const FIXED_IN_BUDGETS = ['INVALID_BUDGET', 'INVALID_CONVERSION_EVENT'];

export function destinationForErrors(rowErrors) {
    const codes = Object.values(rowErrors || {}).flat().map(error => error.code);
    if (codes.length && codes.every(code => FIXED_IN_BUDGETS.includes(code))) return '/onboarding/presupuestos';
    return '/onboarding/clientes';
}
